import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, AlertCircle, Building2, Users, ArrowRight } from 'lucide-react';
import { InvitationService } from '@/services/invitation-service';
import { useAuth } from '@/contexts/auth-context';
import { getWorkspaceUrl, getWorkspaceUrlFromObject } from '@/utils/tenant-utils';
import { checkAuthConflicts } from '@/utils/auth-utils';

export const InviteAccept = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const initializeInvitePage = async () => {
      if (!token) return;
      
      // Check for auth conflicts first - this can cause issues with invitations
      console.log('🔍 Checking for auth conflicts before loading invitation...');
      const conflictResolved = await checkAuthConflicts();
      if (conflictResolved) {
        console.log('✅ Auth conflict resolved');
        // Small delay to let auth state settle
        setTimeout(() => {
          loadInvitation();
        }, 500);
      } else {
        loadInvitation();
      }
    };

    initializeInvitePage();
  }, [token]);

  // Check if user is returning from verification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verifiedParam = urlParams.get('verified');
    if (verifiedParam === 'true') {
      setVerified(true);
    }
  }, []);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const invitationData = await InvitationService.getInvitationByToken(token!);
      
      if (!invitationData) {
        setError('Invalid or expired invitation link');
        return;
      }

      if (invitationData.status !== 'pending') {
        setError('This invitation has already been used or revoked');
        return;
      }

      if (new Date(invitationData.expires_at) < new Date()) {
        setError('This invitation has expired');
        return;
      }

      setInvitation(invitationData);
    } catch (error) {
      console.error('Error loading invitation:', error);
      setError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!token) return;

    try {
      setAccepting(true);
      const result = await InvitationService.acceptInvitation(token);

      if (result.success) {
        setSuccess(true);
        // Redirect to workspace after a short delay
        setTimeout(() => {
          const workspaceUrl = result.workspace?.domain && result.workspace?.domain_verified
            ? `https://${result.workspace.domain}`
            : getWorkspaceUrl(result.workspace?.slug || '');
          
          // Add a parameter to indicate this is from invitation acceptance
          const urlWithParam = workspaceUrl + (workspaceUrl.includes('?') ? '&' : '?') + 'invitation-accepted=true&t=' + Date.now();
          
          // Force a full page load to refresh all workspace data
          // This ensures the new workspace membership is reflected in all contexts
          window.location.href = urlWithParam;
        }, 2000);
      } else {
        setError(result.error || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleSignIn = () => {
    // Redirect to sign in with return URL
    const returnUrl = encodeURIComponent(window.location.href);
    navigate(`/auth?returnUrl=${returnUrl}&mode=signin`);
  };

  const handleSignUp = () => {
    // Redirect to signup with invitation context
    const returnUrl = encodeURIComponent(window.location.href);
    const invitationEmail = invitation?.email;
    navigate(`/auth?mode=signup&email=${encodeURIComponent(invitationEmail)}&invitationId=${invitation?.id}&returnUrl=${returnUrl}`);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'workspace_admin':
        return <Badge variant="destructive">Admin</Badge>;
      case 'workspace_member':
        return <Badge variant="secondary">Member</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'workspace_admin':
        return 'Full workspace control, billing management, and user administration';
      case 'workspace_member':
        return 'Can upload data, create chat channels, and contribute to discussions';
      default:
        return 'Standard workspace access';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <CardTitle className="text-red-600">Invitation Error</CardTitle>
            <CardDescription>
              There was a problem with your invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="mt-4 text-center">
              <Button onClick={() => navigate('/')} variant="outline">
                Return to WattFactory
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-green-600">Welcome to the Team!</CardTitle>
            <CardDescription>
              You've successfully joined {invitation?.workspace?.name}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Redirecting you to your new workspace...
            </p>
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <CardTitle>You're Invited!</CardTitle>
          <CardDescription>
            Join {invitation?.workspace?.name} on WattFactory
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Workspace Details */}
          <div className="text-center">
            <h3 className="font-semibold text-lg mb-2">{invitation?.workspace?.name}</h3>
            <p className="text-sm text-muted-foreground mb-3">
              {invitation?.workspace?.slug}.wattfactory.tech
            </p>
            <div className="flex items-center justify-center gap-2 mb-4">
              {getRoleBadge(invitation?.role)}
            </div>
            <p className="text-sm text-muted-foreground">
              {getRoleDescription(invitation?.role)}
            </p>
          </div>

          {/* Inviter Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  Invited by {invitation?.inviter?.display_name || invitation?.inviter?.email}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(invitation?.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Verification Success Message */}
          {verified && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Email Verified Successfully!
                  </p>
                  <p className="text-xs text-green-600">
                    Your email has been verified. You can now accept the invitation.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {user && user.email && user.id ? (
              // User is properly logged in - can accept immediately
              <Button
                onClick={handleAcceptInvitation}
                disabled={accepting}
                className="w-full"
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining Workspace...
                  </>
                ) : (
                  <>
                    Accept Invitation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            ) : (
              // User needs to sign in first
              <div className="space-y-3">
                <Button onClick={handleSignIn} className="w-full">
                  Sign In to Accept
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or
                    </span>
                  </div>
                </div>
                <Button onClick={handleSignUp} variant="outline" className="w-full">
                  Create Account
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  You'll need a WattFactory account to accept this invitation
                </p>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="text-xs text-muted-foreground text-center space-y-1">
            <p>This invitation expires in 7 days</p>
            <p>You can leave the workspace at any time</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 